import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { ChatMessageAttachment } from 'tripPlanner/api'
import { createChat, fileToChatMessageAttachment } from 'tripPlanner/api'
import { submitMessage, subscribeChatStream, cancelMessage, ChatBusyError, StreamingUnavailableError } from 'tripPlanner/api/v3'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { isAbortError } from 'api/requestUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { StreamMachineEvent } from './useStreamMachine'
import type { StreamHandlers } from './useStreamEventProcessor'
import { isNetworkError, getStreamErrorDisplayMessage } from 'tripPlanner/utils/networkError'
import {
  trackStreamStart,
  trackStreamComplete,
  trackStreamError,
  trackStreamResumed,
  trackReconnect,
} from 'tripPlanner/utils/streamAnalytics'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import type { UserData } from '../../types'

interface StreamConfig {
  chatId: number
  content: string
  userMessageId: string
  assistantMessageId: string
  regenerateMessageId?: string
  attachments?: Array<ChatMessageAttachment>
  /** v3 resume: set to skip submitMessage and subscribe from cursor '0' instead */
  resumeAssistantMessageId?: string
}

interface UseStreamConnectionArgs {
  // Machine
  send: (event: StreamMachineEvent) => void
  // Message overlay
  setPendingUserMessage: (message: MessageItem | undefined) => void
  setStreamingMessage: (message: MessageItem | undefined) => void
  invalidateChat: (forChatId?: number) => Promise<{ skipped: boolean }>
  // Event processor
  createHandlers: (generation: number) => StreamHandlers
  resetBuffer: () => void
  // Scroll
  scrollToNewMessage: (messageId: string) => void
  recalculateSpacerAfterStream: () => void
  // External callbacks
  refetchChatList: () => Promise<unknown>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  onStreamComplete?: (chatId: number) => void
  // Config
  chatId: number | undefined
  setChatId: (id: number | undefined) => void
  prompt: string | undefined
  isLoggedIn: boolean
  isStreaming: boolean
  orderIds?: Array<string>
  files: Array<File>
  setFiles: (files: Array<File>) => void
  userData: UserData
  messages: Array<MessageItem>
  messagesRef: React.MutableRefObject<Array<MessageItem>>
  isStreamingRef: React.MutableRefObject<boolean>
  messagesReady: boolean
  latestGenerationRef: React.MutableRefObject<number>
  // Streaming message ID ref (shared with event processor)
  streamingMessageIdRef: React.MutableRefObject<string | undefined>
}

export interface UseStreamConnectionReturn {
  handleSubmit: (message: string, options?: { messageType?: string }) => Promise<void>
  handleRegenerateResponse: () => Promise<void>
  handleStopStreaming: () => Promise<boolean>
  handleRetry: (config: { chatId: number; userMessageId: string; assistantMessageId: string; content: string }) => Promise<void>
  resetStreamingState: () => void
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useStreamConnection({
  send,
  setPendingUserMessage,
  setStreamingMessage,
  invalidateChat,
  createHandlers,
  resetBuffer,
  scrollToNewMessage,
  recalculateSpacerAfterStream,
  refetchChatList,
  startChatTitleGeneration,
  updateChatLastActivityTimestamp,
  onStreamComplete,
  chatId,
  setChatId,
  prompt,
  isLoggedIn,
  isStreaming,
  orderIds,
  files,
  setFiles,
  userData,
  messages,
  messagesRef,
  isStreamingRef,
  messagesReady,
  latestGenerationRef,
  streamingMessageIdRef,
}: UseStreamConnectionArgs): UseStreamConnectionReturn {
  const intl = useIntl()
  const accessToken = useAppSelector(state => state.auth.accessToken)
  const streamAbortControllerRef = useRef<AbortController | undefined>(undefined)
  const streamCloseRef = useRef<(() => void) | undefined>(undefined)
  const currentStreamingGenerationRef = useRef<number>(0)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })
  const networkErrorMessage = intl.formatMessage({
    id: 'zru2DO-wO2v0Kg1nYQ15',
    defaultMessage: 'Unable to connect. Please check your internet connection and try again.',
    description: 'Error message when chat fails due to network connectivity issues',
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamCloseRef.current?.()
      streamCloseRef.current = undefined
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort()
        streamAbortControllerRef.current = undefined
      }
    }
  }, [])

  // ── Shared stream execution ──

  const executeStream = useCallback(async(
    config: StreamConfig,
    thisGeneration: number,
    handlers: StreamHandlers,
  ) => {
    const { chatId: streamChatId, content, userMessageId, assistantMessageId, regenerateMessageId, attachments, resumeAssistantMessageId } = config
    const isResume = !!resumeAssistantMessageId
    const overlayCreatedAt = new Date().toISOString()

    streamingMessageIdRef.current = assistantMessageId
    resetBuffer()

    // Placeholder overlay — present in DOM for scroll but zero-height until content arrives.
    // Resume path skips this (overlay already created with PG content by the resume effect).
    if (!isResume) {
      setStreamingMessage({
        id: assistantMessageId,
        parts: [],
        role: 'assistant',
        rawContent: '',
        messageType: 'message',
        createdAt: overlayCreatedAt,
        isPlaceholder: !regenerateMessageId,
      })
    }
    let overlayUpgraded = isResume

    streamCloseRef.current?.()
    streamCloseRef.current = undefined

    const abortController = new AbortController()
    streamAbortControllerRef.current?.abort()
    streamAbortControllerRef.current = abortController

    send({
      type: 'CONNECTED',
      chatId: streamChatId,
      userMessageId,
      assistantMessageId,
      abortController,
    })
    if (isResume) {
      trackStreamResumed(streamChatId)
    } else {
      trackStreamStart(streamChatId)
    }

    let capturedError: unknown
    let cursor = '0'

    try {
      if (!isResume) {
        const requestPayload = regenerateMessageId
          ? {
              chatId: streamChatId,
              content,
              regenerateMessageId,
              userMessageId,
              ...(prompt ? { prompt } : {}),
              ...(userData && { userData }),
            }
          : {
              chatId: streamChatId,
              content,
              userMessageId,
              assistantMessageId,
              ...(prompt ? { prompt } : {}),
              ...(attachments && attachments.length > 0 ? { files: attachments } : {}),
              ...(userData && { userData }),
            }

        const submitResult = await submitMessage(requestPayload, accessToken ?? undefined)
        cursor = submitResult.cursor
      }

      await new Promise<void>((resolve, reject) => {
        // fetchEventSource silently resolves its own internal promise on
        // AbortError — none of our callbacks fire. Without this listener
        // the wrapper promise hangs forever after Stop, leaving the machine
        // stuck in `stopping`.
        abortController.signal.addEventListener('abort', () => resolve(), { once: true })

        let reconnectAttempt = 0

        const sseConnection = subscribeChatStream(
          streamChatId,
          cursor,
          accessToken ?? undefined,
          {
            onEvent: (event, data, id) => {
              if (id) {
                send({ type: 'CURSOR_UPDATE', cursor: id })
              }

              if (event === 'terminal') {
                sseConnection?.close()
                streamCloseRef.current = undefined
                resolve()
                return
              }

              if (event === 'heartbeat' || (event === 'message' && !data)) {
                send({ type: 'HEARTBEAT' })
                return
              }

              if (event === 'submitted') {
                if (overlayUpgraded) {
                  overlayUpgraded = false
                  setStreamingMessage({
                    id: assistantMessageId,
                    parts: [],
                    role: 'assistant',
                    rawContent: '',
                    messageType: 'message',
                    createdAt: overlayCreatedAt,
                    isPlaceholder: true,
                  })
                }
                return
              }

              if (event === 'error') {
                try {
                  const parsed: unknown = JSON.parse(data)
                  const reason = parsed && typeof parsed === 'object' && 'reason' in parsed && typeof parsed.reason === 'string'
                    ? parsed.reason
                    : 'unknown'
                  reject(new Error(`Stream error: ${reason}`))
                } catch {
                  reject(new Error(`Stream error: ${data}`))
                }
                return
              }

              if (event === 'catch_up') {
                try {
                  const parsed: unknown = JSON.parse(data)
                  const assembledContent = parsed && typeof parsed === 'object' && 'assembledContent' in parsed && typeof parsed.assembledContent === 'string'
                    ? parsed.assembledContent
                    : undefined
                  if (assembledContent) {
                    setStreamingMessage({
                      id: assistantMessageId,
                      parts: [{ type: 'text', content: assembledContent }],
                      role: 'assistant',
                      rawContent: assembledContent,
                      messageType: 'message',
                      createdAt: overlayCreatedAt,
                      isPlaceholder: false,
                    })
                    overlayUpgraded = true
                  }
                } catch {
                  // Malformed catch_up — ignore and let the stream continue from live events
                }
                return
              }

              // Upgrade placeholder to visible on first content event
              if (!overlayUpgraded) {
                overlayUpgraded = true
                setStreamingMessage({
                  id: assistantMessageId,
                  parts: [],
                  role: 'assistant',
                  rawContent: '',
                  messageType: 'message',
                  createdAt: overlayCreatedAt,
                  isPlaceholder: false,
                })
              }

              handlers.processJsonlChunk(data + '\n', streamChatId)
            },
            onOpen: () => {
              if (!overlayUpgraded) overlayUpgraded = isResume
              if (reconnectAttempt > 0) {
                trackReconnect(streamChatId, reconnectAttempt)
              }
              reconnectAttempt = 0
              send({ type: 'RECONNECTED' })
              send({ type: 'HEARTBEAT' })
            },
            onError: (_err) => {
              reconnectAttempt++
              send({ type: 'RECONNECTING' })
            },
            onFatalError: (err) => {
              reject(err)
            },
          },
          abortController.signal,
        )
        streamCloseRef.current = sseConnection.close
      })
    } catch(streamError) {
      if (!isAbortError(streamError)) {
        capturedError = streamError
        throw streamError
      }
    } finally {
      // If the stream was aborted (user stop or stale generation), skip full
      // cleanup — handleStopStreaming already handled UI state, and the server
      // may have partially-processed content that shouldn't be fetched yet.
      const wasAborted = abortController.signal.aborted
      const isCurrentAbortController = streamAbortControllerRef.current === abortController
      if (isCurrentAbortController) {
        streamAbortControllerRef.current = undefined
      }

      // Always cancel pending RAFs regardless of how the stream ended
      handlers.dispose()

      const isCurrentGeneration = thisGeneration >= latestGenerationRef.current
      const isError = capturedError !== undefined

      if (isCurrentGeneration && !wasAborted) {
        handlers.flushTrailingBuffer(streamChatId)

        if (!isError) {
          onStreamComplete?.(streamChatId)
          trackStreamComplete(streamChatId, false)
        }

        await invalidateChat(streamChatId)

        if (isError) {
          setStreamingMessage(undefined)
        }

        recalculateSpacerAfterStream()
        streamingMessageIdRef.current = undefined
      }

      if (!isCurrentGeneration) {
        // Stale streams must not reset the machine for a newer stream.
      } else if (isError) {
        // Machine enters error state — preserves context (chatId, messageIds)
        // for potential inline retry. Streaming overlay may still be visible
        // with partial content if the server didn't persist it yet.
        send({
          type: 'ERROR',
          error: capturedError instanceof Error ? capturedError : new Error(String(capturedError)),
        })
      } else {
        send({ type: 'STREAM_END' })
      }
    }
  }, [
    accessToken,
    prompt,
    userData,
    latestGenerationRef,
    send,
    setStreamingMessage,
    invalidateChat,
    resetBuffer,
    recalculateSpacerAfterStream,
    onStreamComplete,
    streamingMessageIdRef,
  ])

  // v3: One-shot resume check — runs once per chatId when messagesReady flips true.
  // Uses refs to avoid stale closures and prevent re-firing on every messages change.
  const hasCheckedResumeRef = useRef(false)
  const previousChatIdRef = useRef(chatId)
  useEffect(() => {
    if (previousChatIdRef.current !== chatId) {
      previousChatIdRef.current = chatId
      hasCheckedResumeRef.current = false
    }
    if (hasCheckedResumeRef.current || !chatId || !messagesReady) return
    hasCheckedResumeRef.current = true

    if (isStreamingRef.current) return

    const currentMessages = messagesRef.current
    let latestAssistant: MessageItem | undefined
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].role === 'assistant') { latestAssistant = currentMessages[i]; break }
    }
    if (!latestAssistant) return

    const status = latestAssistant.generationStatus
    if (status !== 'PENDING' && status !== 'IN_PROGRESS') return

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'RESUME', generation: thisGeneration, chatId, assistantMessageId: latestAssistant.id })

    // Set overlay with PG-checkpointed content (user sees partial immediately)
    setStreamingMessage({
      id: latestAssistant.id,
      parts: latestAssistant.parts,
      role: 'assistant',
      rawContent: latestAssistant.rawContent ?? '',
      messageType: 'message',
      createdAt: latestAssistant.createdAt,
    })

    const handlers = createHandlers(thisGeneration)

    executeStream(
      {
        chatId,
        content: '',
        userMessageId: '',
        assistantMessageId: latestAssistant.id,
        resumeAssistantMessageId: latestAssistant.id,
      },
      thisGeneration,
      handlers,
    ).catch((err) => {
      send({ type: 'ERROR', error: err instanceof Error ? err : new Error(String(err)) })
    })
  }, [chatId, messagesReady, createHandlers, isStreamingRef, messagesRef, send, setStreamingMessage, latestGenerationRef, executeStream])

  // ── Ensure chat exists ──

  const ensureChatId = useCallback(async(message?: string): Promise<number> => {
    if (chatId !== undefined) return chatId

    const chatResponse = await createChat({
      ...(orderIds?.length ? { orderIds } : {}),
    })
    const newChatId = chatResponse.id
    setChatId(newChatId)

    if (isLoggedIn) {
      await refetchChatList()
    }
    if (message) {
      startChatTitleGeneration(newChatId, message)
    }

    return newChatId
  }, [chatId, orderIds, setChatId, isLoggedIn, refetchChatList, startChatTitleGeneration])

  // ── Submit ──

  const handleSubmit = useCallback(async(message: string, options?: { messageType?: string }) => {
    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'SUBMIT', generation: thisGeneration })

    const userMessageId = uuidV4()
    const userMessageCreatedAt = new Date().toISOString()
    const userMessage: MessageItem = {
      id: userMessageId,
      parts: [{ type: 'text', content: message }],
      role: 'user',
      files: [...files],
      rawContent: message,
      messageType: options?.messageType ?? 'message',
      createdAt: userMessageCreatedAt,
    }

    setPendingUserMessage(userMessage)
    scrollToNewMessage(userMessageId)

    const currentFiles = [...files]
    setFiles([])

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId(message)
      updateChatLastActivityTimestamp(currentChatId, new Date().toISOString())

      const assistantMessageId = uuidV4()
      const handlers = createHandlers(thisGeneration)

      // Encode file attachments
      let attachments: Array<ChatMessageAttachment> = []
      if (currentFiles.length > 0) {
        const failedFileNames: Array<string> = []
        const encodedFiles = await Promise.all(
          currentFiles.map(async(file) => {
            try {
              return await fileToChatMessageAttachment(file)
            } catch {
              failedFileNames.push(file.name)
              return undefined
            }
          }),
        )
        attachments = nonNullable(encodedFiles)
        if (failedFileNames.length > 0) {
          showSnackbar(
            intl.formatMessage(
              {
                id: 'aoIXiW41sPchU8OjeWiY',
                defaultMessage: '{count, plural, one {# file} other {# files}} could not be attached: {names}',
                description: 'Warning when one or more files fail to encode for chat attachment',
              },
              { count: failedFileNames.length, names: failedFileNames.join(', ') },
            ),
            'warning',
          )
        }
      }

      executeStreamCalled = true
      await executeStream(
        { chatId: currentChatId, content: message, userMessageId, assistantMessageId, attachments },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      // v3: ChatBusyError → leave input populated, show snackbar
      if (error instanceof ChatBusyError) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      // v3: StreamingUnavailableError → retryable, show snackbar
      if (error instanceof StreamingUnavailableError) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }

      if (!executeStreamCalled) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    latestGenerationRef,
    send,
    files,
    setPendingUserMessage,
    scrollToNewMessage,
    setFiles,
    ensureChatId,
    updateChatLastActivityTimestamp,
    createHandlers,
    executeStream,
    setStreamingMessage,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Regenerate ──

  const handleRegenerateResponse = useCallback(async() => {
    const reversed = [...messages].reverse()
    const lastUserMessage = reversed.find((m) => m.role === 'user')
    if (!lastUserMessage) return
    const assistantMessageToRegenerate = reversed.find((m) => m.role === 'assistant')
    if (!assistantMessageToRegenerate) return

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'REGENERATE', generation: thisGeneration })

    // For regeneration, reuse the existing assistant message ID — ID-based
    // deduplication filters out the old server message so the streaming
    // message visually "replaces" it immediately
    const regenerateAssistantId = assistantMessageToRegenerate.id
    const regenerateUserId = lastUserMessage.id

    setStreamingMessage(undefined)

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId()

      const handlers = createHandlers(thisGeneration)

      executeStreamCalled = true
      await executeStream(
        {
          chatId: currentChatId,
          content: getTextFromParts(lastUserMessage.parts),
          userMessageId: regenerateUserId,
          assistantMessageId: regenerateAssistantId,
          regenerateMessageId: regenerateAssistantId,
        },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      if (error instanceof ChatBusyError) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      if (error instanceof StreamingUnavailableError) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }

      if (!executeStreamCalled) {
        setStreamingMessage(undefined)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'pp-WwVLFlIsUCNNe3QOD',
          defaultMessage: 'Regeneration failed',
          description: 'Snackbar heading when regenerating a chat response fails',
        }),
      })
      throw error
    }
  }, [
    messages,
    latestGenerationRef,
    send,
    setStreamingMessage,
    ensureChatId,
    createHandlers,
    executeStream,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Retry (G4: inline retry with same IDs for server idempotency) ──

  const handleRetry = useCallback(async(config: {
    chatId: number
    userMessageId: string
    assistantMessageId: string
    content: string
  }) => {
    const { chatId: retryChatId, userMessageId, assistantMessageId, content } = config

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'SUBMIT', generation: thisGeneration })

    const handlers = createHandlers(thisGeneration)

    try {
      await executeStream(
        { chatId: retryChatId, content, userMessageId, assistantMessageId },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      if (error instanceof ChatBusyError) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      if (error instanceof StreamingUnavailableError) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }

      // executeStream's finally block already handled machine events and cleanup.
      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(retryChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    latestGenerationRef,
    send,
    createHandlers,
    executeStream,
    setStreamingMessage,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Stop ──

  const handleStopStreaming = useCallback(async() => {
    const hadActiveStream = !!streamCloseRef.current || !!streamAbortControllerRef.current || isStreaming
    if (!hadActiveStream) return false

    // Close SSE first to stop receiving events, then cancel server-side
    streamCloseRef.current?.()
    streamCloseRef.current = undefined
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = undefined
    }

    if (chatId !== undefined) {
      try {
        await cancelMessage(chatId, accessToken ?? undefined)
      } catch {
        // Server cancel failed — already closed locally
      }
    }

    currentStreamingGenerationRef.current = 0
    send({ type: 'STOP' })
    streamingMessageIdRef.current = undefined

    if (chatId !== undefined) {
      trackStreamComplete(chatId, true)

      // Refresh React Query cache so the cancelled message is in server messages.
      // Without this, the next message's overlay replacement causes the cancelled
      // message to vanish (it only existed as a streaming overlay, never fetched).
      // Overlay cleanup happens in useChatMessages once the server message is
      // actually present in rendered query data, avoiding a brief empty frame.
      invalidateChat(chatId).catch(() => {})
    }

    return true
  }, [chatId, accessToken, isStreaming, send, streamingMessageIdRef, invalidateChat])

  // ── Reset ──

  const resetStreamingState = useCallback(() => {
    streamCloseRef.current?.()
    streamCloseRef.current = undefined
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = undefined
    }
    streamingMessageIdRef.current = undefined
    send({ type: 'RESET' })
  }, [streamingMessageIdRef, send])

  return { handleSubmit, handleRegenerateResponse, handleStopStreaming, handleRetry, resetStreamingState }
}
